import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getAppointmentsApi, deleteAppointmentApi, type AppointmentData } from '../api/appointmentApi';
import { checkDuplicateAppointment } from '../../shared/api/appointmentApi';
import { TrashIcon } from './icons/TrashIcon';
import { EditIcon } from './icons/EditIcon';

interface DraftAppointmentsProps {
  onContinueBooking: (draft: AppointmentData) => void;
  onDraftCountChange?: (count: number) => void;
}

// Helper component for duplicate appointment message
const CheckDuplicateAppointmentMessage: React.FC<{ 
  idCard: string; 
  serviceId?: string; 
  onShowDate: (appointment: AppointmentData) => void;
}> = ({ idCard, serviceId, onShowDate }) => {
  const [found, setFound] = useState<AppointmentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    checkDuplicateAppointment(idCard, serviceId || '', 'admin').then((apt) => {
      if (mounted) setFound(apt);
    }).finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [idCard, serviceId]);

  if (loading) return <span>בודק תורים קיימים...</span>;
  if (!found) return null;
  return (
    <span>
      למטופל זה כבר קיים תור במערכת.
      <button
        className="ml-2 underline text-blue-700 hover:text-blue-900 text-xs"
        onClick={() => { setShow(!show); onShowDate(found); }}
      >
        הצג תאריך
      </button>
      {show && (
        <span className="ml-2 text-xs text-gray-700">
          {found.selectedDayLabel || found.selectedDay} {found.selectedTime ? `בשעה ${found.selectedTime}` : ''}
        </span>
      )}
    </span>
  );
};

export const DraftAppointments: React.FC<DraftAppointmentsProps> = ({ onContinueBooking, onDraftCountChange }) => {
  const [duplicateInfo, setDuplicateInfo] = useState<{ appointment: AppointmentData | null, draftId: string | null }>({ appointment: null, draftId: null });
  const [drafts, setDrafts] = useState<AppointmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'us' | 'xray' | 'general'>('all');
  const location = useLocation();

  useEffect(() => {
    loadDrafts();
  }, [location.pathname]); // Reload when navigating to this page

  const loadDrafts = async () => {
    try {
      setLoading(true);
      setError(null);
      const appointments = await getAppointmentsApi({ status: 'draft' });
      setDrafts(appointments.sort((a, b) => 
        new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      ));
      onDraftCountChange?.(appointments.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה בטעינת טיוטות');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (appointmentId: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק טיוטה זו?')) {
      return;
    }

    try {
      setDeletingId(appointmentId);
      await deleteAppointmentApi(appointmentId);
      const updatedDrafts = drafts.filter(d => d.appointmentId !== appointmentId);
      setDrafts(updatedDrafts);
      onDraftCountChange?.(updatedDrafts.length);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'שגיאה במחיקת הטיוטה');
    } finally {
      setDeletingId(null);
    }
  };

  const getProgressStep = (draft: AppointmentData): string => {
    if (!draft.serviceType) return 'שלב 2: בחירת סוג שירות';
    if (draft.serviceType === 2 && !draft.serviceId) return 'שלב 3: בחירת שירות';
    if (!draft.selectedDay) return 'שלב 4: בחירת תאריך';
    if (!draft.selectedTime) return 'שלב 5: בחירת שעה';
    return 'מוכן להשלמה';
  };

  const getServiceTypeName = (serviceType?: number | string): string => {
    const type = typeof serviceType === 'string' ? parseInt(serviceType, 10) : serviceType;
    if (type === 2) return 'אולטרסאונד (US)';
    if (type === 1) return 'רנטגן (Xray)';
    if (type === 0) return 'לא נבחר';
    return 'לא נבחר'; // Handle undefined/null
  };

  const getServiceTypeCategory = (serviceType?: number | string): 'us' | 'xray' | 'general' => {
    const type = typeof serviceType === 'string' ? parseInt(serviceType, 10) : serviceType;
    if (type === 2) return 'us';
    if (type === 1) return 'xray';
    return 'general'; // Handle 0, undefined, or null
  };

  const filteredDrafts = drafts.filter(draft => {
    if (filterType === 'all') return true;
    return getServiceTypeCategory(draft.serviceType) === filterType;
  });

  const countByType = {
    all: drafts.length,
    us: drafts.filter(d => {
      const type = typeof d.serviceType === 'string' ? parseInt(d.serviceType, 10) : d.serviceType;
      return type === 2;
    }).length,
    xray: drafts.filter(d => {
      const type = typeof d.serviceType === 'string' ? parseInt(d.serviceType, 10) : d.serviceType;
      return type === 1;
    }).length,
    general: drafts.filter(d => {
      const type = typeof d.serviceType === 'string' ? parseInt(d.serviceType, 10) : d.serviceType;
      return !type || type === 0 || isNaN(type);
    }).length,
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">טוען טיוטות...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadDrafts}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          נסה שוב
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">תורים לא הושלמו</h1>
        <p className="text-gray-600 mt-1">
          {drafts.length} {drafts.length === 1 ? 'טיוטה' : 'טיוטות'}
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2 flex-wrap">
        <button
          onClick={() => setFilterType('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filterType === 'all'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          הכל ({countByType.all})
        </button>
        <button
          onClick={() => setFilterType('us')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filterType === 'us'
              ? 'bg-teal-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🔊 אולטרסאונד ({countByType.us})
        </button>
        <button
          onClick={() => setFilterType('xray')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filterType === 'xray'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📸 רנטגן ({countByType.xray})
        </button>
        <button
          onClick={() => setFilterType('general')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filterType === 'general'
              ? 'bg-gray-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ❓ כללי ({countByType.general})
        </button>
      </div>

      {filteredDrafts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            {filterType === 'all' ? 'אין טיוטות' : `אין טיוטות מסוג ${
              filterType === 'us' ? 'אולטרסאונד' : 
              filterType === 'xray' ? 'רנטגן' : 
              'כללי'
            }`}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    שם מלא
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    טלפון
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ישוב
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מרפאה / נוצר ע"י
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    שירות
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סוג שירות
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    התקדמות
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDrafts.map((draft) => (
                  <React.Fragment key={draft.appointmentId}>
                    {/* Duplicate appointment check message */}
                    {draft.idCard && (
                      <tr>
                        <td colSpan={8} className="px-6 py-2 bg-yellow-50 text-yellow-900 text-xs">
                          <CheckDuplicateAppointmentMessage
                            idCard={draft.idCard}
                            serviceId={draft.serviceId}
                            onShowDate={(appointment) => setDuplicateInfo({ appointment, draftId: draft.appointmentId || null })}
                          />
                        </td>
                      </tr>
                    )}
                    <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span>{draft.fullName || '-'}</span>
                        <span className="text-xs text-gray-500 mt-1">{draft.idCard || '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {draft.phoneNumber || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {draft.village || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span className="font-medium">{draft.clinicName || '-'}</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          {draft.createdBy && (
                            <span className="text-xs text-gray-500">{draft.createdBy}</span>
                          )}
                          {draft.createdAt && (
                            <span className="text-xs text-gray-400">• {formatDate(draft.createdAt)}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {draft.serviceName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        draft.serviceType === 2 
                          ? 'bg-teal-100 text-teal-800'
                          : draft.serviceType === 1
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {getServiceTypeName(draft.serviceType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {getProgressStep(draft)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        {getServiceTypeCategory(draft.serviceType) !== 'xray' && (
                          <button
                            onClick={() => onContinueBooking(draft)}
                            className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                            title="המשך הזמנה"
                          >
                            <EditIcon className="w-4 h-4" />
                            <span>המשך</span>
                          </button>
                        )}
                        <button
                          onClick={() => draft.appointmentId && handleDelete(draft.appointmentId)}
                          disabled={deletingId === draft.appointmentId}
                          className="text-red-600 hover:text-red-900 flex items-center gap-1 disabled:opacity-50"
                          title="מחק טיוטה"
                        >
                          <TrashIcon className="w-4 h-4" />
                          <span>{deletingId === draft.appointmentId ? 'מוחק...' : 'מחק'}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
